import { useEffect, useMemo, useState } from "react";
import ArrowLeftLineIcon from "remixicon-react/ArrowLeftLineIcon";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import Papa from "papaparse";
import CheckLineIcon from "remixicon-react/CheckLineIcon";
import CloseLineIcon from "remixicon-react/CloseLineIcon";
import DownloadCloud2sLineIcon from "remixicon-react/DownloadCloud2LineIcon";
import networkActions from "../../redux/network/actions";
import CardLayout from "../../components/utils/CardLayout";
import Button from "../../components/utils/Button";
import Table from "../../components/utils/Table";
import { isKeyUnique } from "../../helpers/commonFunction";
import authActions from "../../redux/auth/actions";
import rechargeRequestAction from "../../redux/rechargeRequest/actions";

const {
  createRechargeRequest,
  getRecharge,
  checkValidMsisdn,
  getNetworksForDealerRecharge,
} = rechargeRequestAction;

const { getUserDataByToken } = authActions;
  const { getActiveNetwork } = networkActions;
const BASE_URL = process.env.REACT_APP_SITE_BASE_URL;

const MDNForm = ({ back, search }) => {
  const dispatch = useDispatch();
  const { networkResult } = useSelector((state) => state.network);

  const {
    createRechargeReuestLoading,
    createRechargeReuestError,
    createRechargeReuestMessage,
    getRechargeList,
    checkValidMsisdnData,
    checkValidMsisdnLoading,
    getNetworksForDealerRechargeList,
  } = useSelector((state) => state.rechargeRequest);

  const [previewData, setPreviewData] = useState([]);
  const [activationRequests, setActivationRequests] = useState([]);
  const [networkId, setNetworkId] = useState(0);
  const [planAmount, setPlanAmount] = useState(0);
  const [showUploadFile, setShowUploadFile] = useState(false);
  const [apiCall, setApiCall] = useState(false);


  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    clearErrors,
    setValue,
    setError,
  } = useForm({
    defaultValues: {
      networkID: "",
      planID: "",
    },
  });

  const watchedPlanID = watch("planID");

  /*
   * ================================
   * SELECTED PLAN
   * ================================
   */
  const selectedPlan = useMemo(() => {
    if (watchedPlanID > 0) {
      return getRechargeList.filter((item) => item.ID == watchedPlanID);
    }

    return [];
  }, [watchedPlanID, getRechargeList]);

  /*
   * ================================
   * SET PLAN PRICE
   * ================================
   */
  useEffect(() => {
    if (selectedPlan.length > 0 && selectedPlan[0].RechargePrice > 0) {
      setValue("price", selectedPlan[0].RechargePrice);
    }
  }, [selectedPlan, setValue]);

  /*
   * ================================
   * CREATE REQUEST RESPONSE
   * ================================
   */
  useEffect(() => {
    if (createRechargeReuestError || createRechargeReuestMessage) {
      back();
    }
  }, [createRechargeReuestError, createRechargeReuestMessage, back]);

  /*
   * ================================
   * INITIAL API CALL
   * ================================
   */
  useEffect(() => {
    dispatch(getNetworksForDealerRecharge());
    dispatch(getUserDataByToken());
  }, [dispatch]);

  /*
   * ================================
   * CHECK MSISDN API
   * ================================
   */
  useEffect(() => {
    if (!apiCall || previewData.length === 0) {
      return;
    }

    const validData = previewData.filter((item) => item.isValid === 1);

    if (validData.length === 0) {
      setApiCall(false);
      return;
    }

    const msisdnArray = validData.map((item) => item.msisdn);

    dispatch(checkValidMsisdn(msisdnArray));
  }, [apiCall, previewData, dispatch]);

  /*
   * ================================
   * CHECK MSISDN RESPONSE
   * ================================
   */
  useEffect(() => {
    if (!checkValidMsisdnData || checkValidMsisdnData.length === 0) {
      return;
    }

    setApiCall(false);

    /*
     * Update Preview Data
     */
    const newPreviewData = previewData.map((item) => {
      const result = checkValidMsisdnData.find(
        (item1) => item1.MSISDN === item.msisdn,
      );

      if (!result) {
        return item;
      }

      return {
        ...item,

        isValid: result.Status === 0 ? 0 : item.isValid,

        message: result.ExistStatus || item.message,
      };
    });

    setPreviewData(newPreviewData);

    /*
     * Update Request Data
     */
    const newRequestData = activationRequests.map((item) => {
      const result = checkValidMsisdnData.find(
        (item1) => item1.MSISDN === item.msisdn,
      );

      if (result && result.Status === 0) {
        return {
          ...item,
          status: 0,
          reason: result.ExistStatus,
        };
      }

      return item;
    });

    setActivationRequests(newRequestData);
  }, [checkValidMsisdnData]);

  /*
   * ================================
   * NETWORK CHANGE
   * ================================
   */
  const handleNetwork = (e) => {
    e.preventDefault();

    setNetworkId(e.target.value);

    setValue("planID", "");
    setValue("price", "");

    setPlanAmount(0);

    dispatch(getRecharge());

    setShowUploadFile(true);
  };

  /*
   * ================================
   * CSV FILE CHANGE
   * ================================
   */
  const handleFileChange = (e) => {
    clearErrors("uploadedFile");

    setPreviewData([]);
    setActivationRequests([]);
    setApiCall(false);

    const file = e.target.files?.[0];

    /*
     * File required
     */
    if (!file) {
      setError("uploadedFile", {
        type: "custom",
        message: "Csv is required!",
      });

      return;
    }

    /*
     * File extension
     */
    const fileNameArr = file.name.split(".");
    const fileExt = fileNameArr[fileNameArr.length - 1];

    if (fileExt.toLowerCase() !== "csv") {
      setError("uploadedFile", {
        type: "custom",
        message: "Invalid file type. Only CSV file allowed.",
      });

      return;
    }

    const fileReader = new FileReader();

    fileReader.onload = ({ target }) => {
      /*
       * Parse CSV
       */
      const csvOutput = Papa.parse(target.result, {
        header: true,
        skipEmptyLines: true,
      });

      const parsedData = csvOutput?.data;

      /*
       * Empty CSV
       */
      if (!parsedData || parsedData.length === 0) {
        setError("uploadedFile", {
          type: "custom",
          message: "CSV file is empty.",
        });

        return;
      }

      /*
       * Maximum 100 rows
       */
      if (parsedData.length > 100) {
        setError("uploadedFile", {
          type: "custom",
          message: "Maximum 100 rows are allowed",
        });

        return;
      }

      /*
       * CSV Columns
       */
      const columns = Object.keys(parsedData[0]);

      /*
       * Required CSV columns
       */
      const specifiedColumns = ["MSISDN", "SERIAL NUMBER", "ZIPCODE"];

      /*
       * Header validation
       */
      const isValidCsvHeading =
        specifiedColumns.every((item) => columns.includes(item)) &&
        columns.length === specifiedColumns.length;

      if (!isValidCsvHeading) {
        setError("uploadedFile", {
          type: "custom",
          message:
            "Invalid CSV. Required columns: MSISDN, SERIAL NUMBER, ZIPCODE",
        });

        return;
      }

      /*
       * ================================
       * VALIDATE EVERY ROW
       * ================================
       */
      const dataSource = parsedData.map((item) => {
        let error = false;
        let message = "";

        /*
         * IMPORTANT:
         * Keep these as STRING.
         */
        const msisdn = item.MSISDN?.trim();

        const serialNumber = item["SERIAL NUMBER"]?.trim();

        const zipcode = item.ZIPCODE?.trim();

        /*
         * MSISDN
         * Exactly 10 digits
         */
        if (!msisdn) {
          error = true;
          message = "MSISDN is required";
        } else if (!/^\d{10}$/.test(msisdn)) {
          error = true;
          message = "MSISDN must be 10 digits";
        } else if (!serialNumber) {

        /*
         * SERIAL NUMBER
         * Exactly 19 digits
         */
          error = true;
          message = "Serial Number is required";
        } else if (!/^\d{19}$/.test(serialNumber)) {
          error = true;
          message = "Serial Number must be 19 digits";
        } else if (!zipcode) {

        /*
         * ZIPCODE
         * Exactly 6 digits
         */
          error = true;
          message = "Zipcode is required";
        } else if (!/^\d{6}$/.test(zipcode)) {
          error = true;
          message = "Zipcode must be 6 digits";
        }

        return {
          msisdn,
          serialNumber,
          zipcode,
          isValid: error ? 0 : 1,
          message,
        };
      });

      /*
       * ================================
       * DUPLICATE MSISDN
       * ================================
       */
      if (!isKeyUnique(dataSource, "msisdn")) {
        setError("uploadedFile", {
          type: "custom",
          message: "Csv contains duplicate MSISDN",
        });

        return;
      }

      /*
       * ================================
       * DUPLICATE SERIAL NUMBER
       * ================================
       */
      if (!isKeyUnique(dataSource, "serialNumber")) {
        setError("uploadedFile", {
          type: "custom",
          message: "Csv contains duplicate Serial Number",
        });

        return;
      }

      /*
       * Set preview
       */
      setPreviewData(dataSource);

      /*
       * ================================
       * REQUEST DATA
       * ================================
       */
      const requestData = dataSource.map((item) => {
        return {
          msisdn: item.msisdn,
          serialNumber: item.serialNumber,
          zipcode: item.zipcode,
          status: item.isValid,
          reason: item.message,
        };
      });

      setActivationRequests(requestData);

      /*
       * Call backend MSISDN validation
       * only when valid CSV rows exist
       */
      const validRows = dataSource.filter((item) => item.isValid === 1);

      if (validRows.length > 0) {
        setApiCall(true);
      }
    };

    fileReader.readAsText(file);
  };

  /*
   * ================================
   * PREVIEW COLUMNS
   * ================================
   */
  const previewColumns = [
    {
      displayName: "MSISDN",
      key: "msisdn",
    },
    {
      displayName: "SERIAL NUMBER",
      key: "serialNumber",
    },
    {
      displayName: "ZIPCODE",
      key: "zipcode",
    },
    {
      displayName: "VALID",
      key: "Valid",
    },
    {
      displayName: "REASON",
      key: "errorReason",
    },
  ];

  /*
   * ================================
   * TABLE DATA
   * ================================
   */
  const dataSource =
    previewData?.length > 0
      ? previewData.map((item) => {
          return {
            msisdn: item.msisdn,

            serialNumber: item.serialNumber,

            zipcode: item.zipcode,

            Valid:
              item.isValid === 1 ? (
                <CheckLineIcon color="#2ecc71" />
              ) : (
                <CloseLineIcon color="#e74c3c" />
              ),

            errorReason:
              item.isValid === 0 ? (
                <span
                  style={{
                    color: "#e74c3c",
                  }}
                >
                  {item.message}
                </span>
              ) : (
                ""
              ),
          };
        })
      : [];

  /*
   * ================================
   * DOWNLOAD SAMPLE FILE
   * ================================
   */
  const downloadSampleFile = () => {
    const url = `${BASE_URL}/assets/csv/mdn-swap-sample-file.csv`;

    const link = document.createElement("a");

    link.href = url;

    link.download = "MDN Swap Sample File.csv";

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  };

  /*
   * ================================
   * SUBMIT
   * ================================
   */
  const handleAddRechargeRequest = (data) => {
    delete data.uploadedFile;

    /*
     * Count valid records
     */
    const successCount = activationRequests.filter(
      (item) => item.status === 1,
    ).length;

    if (successCount <= 0) {
      alert("Invalid data for upload");

      return;
    }

    const req = {
      ...data,
      rechargeData: activationRequests,
    };
    console.log();

    // dispatch(
    //     createRechargeRequest(
    //         req,
    //         search
    //     )
    // );
  };
//   const isSubmitDisabled =
//   previewData.length === 0 ||
//   checkValidMsisdnLoading ||
//   previewData.some((item) => item.isValid !== 1);

  return (
    <CardLayout title="MDN Swap">
      {/* Header */}
      <div className="row mb-3">
        <div className="col-lg-12 text-right">
          <button
            type="button"
            onClick={downloadSampleFile}
            style={{
              background: "#2ecc71",
              color: "#fff",
              pointerEvents: "auto",
            }}
            className="btn font-weight-bold mr-2"
          >
            <DownloadCloud2sLineIcon size={16} />
            Sample File
          </button>

          <button
            type="button"
            onClick={back}
            className="btn btn-danger font-weight-bold"
          >
            <ArrowLeftLineIcon size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="container">
        <form onSubmit={handleSubmit(handleAddRechargeRequest)}>
          <div className="form-row">
            {/* Network / Plan code
                            can be added here if required */}
          </div>

          {/* Upload CSV */}
          <div className="row mb-3">
            {/* NETWORK */}
            <div className="col-lg-2 col-md-4 mb-2">
                <label>Select Network</label>

                <select className="form-control" {...register("networkID")}>
                  <option value="">All</option>

                  {networkResult
                    ?.filter((item) => item.IsActive == 1)
                    .map((item) => (
                      <option value={item.ID} key={item.ID}>
                        {item.NetworkName}
                      </option>
                    ))}
                </select>
            </div>

            {/* UPLOAD FILE */}
            <div className="col-md-6">
              <label htmlFor="csvFile">
                <span className="text-danger">*</span>
                Upload File
              </label>

              <input
                type="file"
                className="form-control-file"
                name="uploadedFile"
                id="csvFile"
                accept=".csv"
                {...register("uploadedFile", {
                  required: "Csv is required",
                })}
                onChange={handleFileChange}
              />

              <small className="text-danger d-block">
                Note : Only 100 Rows are allowed
              </small>

              {errors?.uploadedFile && (
                <div className="invalid-feedback d-block">
                  {errors.uploadedFile.message}
                </div>
              )}
            </div>
          </div>

          {/* Preview */}
          {previewData.length > 0 && (
            <>
              <hr />

              <div className="col-md-12 mb-3 p-0 recharge-form-tble">
                <Table
                  columns={previewColumns}
                  loading={checkValidMsisdnLoading}
                  data={dataSource}
                  pagination={false}
                />
              </div>
            </>
          )}

          <hr />

          {/* Footer */}
          <div className="text-right row">
            <div className="col-md-12">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                onClick={back}
              >
                Close
              </button>

              <Button
                title="Submit"
                style={{
                  minWidth: "80px",
                }}
                //disabled={isSubmitDisabled}
                loading={createRechargeReuestLoading || checkValidMsisdnLoading}
                buttonClass="btn btn-primary"
                buttonType="submit"
              />
            </div>
          </div>
        </form>
      </div>
    </CardLayout>
  );
};

export default MDNForm;
